<template>
  <!-- 日報の個別表示画面（日報IDの入力・URL の日報IDで表示。SPEC-045） -->
  <v-container>
    <!-- 日報IDの入力欄 -->
    <v-card class="mb-4">
      <v-card-text>
        <v-form @submit.prevent="onSubmit">
          <v-row align="start" no-gutters>
            <v-col cols="12" sm="auto" class="mr-sm-4">
              <v-text-field
                v-model="inputReportId"
                label="日報ID"
                inputmode="numeric"
                autocomplete="off"
                :error-messages="inputError"
                @input="inputError = ''"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="auto" class="pt-sm-3">
              <v-btn color="primary" type="submit">表示</v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>

    <!-- 取得できなかった場合のメッセージ -->
    <v-alert v-if="errorMessage" type="error" text>{{ errorMessage }}</v-alert>

    <v-card v-if="loading || isFetched">
      <loading-view :loading="loading" />

      <template v-if="!loading && isFetched">
        <v-list-item>
          <ProfileImageView :staff-cd="userEntry.staffCd"></ProfileImageView>
          <v-list-item-content class="ml-3">
            <v-list-item-title class="headline">{{
              userEntry.userName
            }}</v-list-item-title>
            <v-list-item-subtitle>{{
              userEntry.groupName
            }}</v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>

        <!-- 標準日報 -->
        <ReportDetail :report-id="reportId" />

        <!-- A,B共通のツールボックスとコメント-->
        <ReportComments :report-id="reportId" />

        <!-- 転送の履歴 転送可能な人だけ表示 -->
        <ReportForwards :report-id="reportId" />
      </template>
    </v-card>
  </v-container>
</template>

<script>
import Common from "@/components/Common";
import ReportAPI from "@/apis/ReportAPI";
import UserAPI from "@/apis/UserAPI";
import ReportViewSupport from "../components/ReportViewSupport";
import ProfileImageView from "../components/controls/ProfileImageView";
import ReportComments from "./sub/ReportComments";
import ReportDetail from "./sub/ReportDetail";
import ReportForwards from "./sub/ReportForwards";

export default {
  mixins: [Common, ReportViewSupport],
  components: {
    ReportDetail,
    ReportForwards,
    ReportComments,
    ProfileImageView,
  },
  mounted: function () {
    this.setupConditions();
  },
  props: ["reportId"],
  watch: {
    $route: function (to, from) {
      this.setupConditions();
    },
  },
  data() {
    return {
      loading: false,

      // 日報IDの入力欄の値・入力エラー・取得できなかった場合のメッセージ
      inputReportId: "",
      inputError: "",
      errorMessage: "",

      fetched: {
        reportId: "",
      },

      // 以下表示データ
      userEntry: {
        staffCd: "",
        userName: "",
        groupName: "",
      },
    };
  },
  computed: {
    isFetched: function () {
      return !!this.reportId && this.reportId == this.fetched.reportId;
    },
    entry() {
      return this.$store.state.reports[this.reportId];
    },
  },
  methods: {
    setupConditions() {
      this.inputReportId = this.reportId || "";
      this.inputError = "";
      this.errorMessage = "";

      // /report（日報IDなし）は入力欄だけを表示する
      if (!this.reportId) {
        this.fetched.reportId = "";
        return;
      }
      // URL の日報IDが数字でない場合は API を呼ばない
      const id = this.normalizeReportId(this.reportId);
      if (id === null) {
        this.fetched.reportId = "";
        this.inputError = "日報IDは数字で入力してください";
        return;
      }
      this.fetchData(true);
    },

    /**
     * 日報IDの入力値を正規化する。
     * 前後の空白（全角を含む）を除き、全角数字を半角にする。
     * @param {string} value 入力値
     * @returns {string|null} 正の整数の文字列。数字でない・0 の場合は null。空の場合は ""
     */
    normalizeReportId(value) {
      const text = String(value == null ? "" : value)
        .replace(/^[\s\u3000]+|[\s\u3000]+$/g, "")
        .replace(/[０-９]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0));
      if (text === "") return "";
      if (!/^[0-9]+$/.test(text) || Number(text) <= 0) return null;
      return String(Number(text));
    },

    // 「表示」ボタン（入力欄で Enter キーでも呼ばれる）
    onSubmit() {
      const id = this.normalizeReportId(this.inputReportId);
      if (id === "") {
        this.inputError = "日報IDを入力してください";
        return;
      }
      if (id === null) {
        this.inputError = "日報IDは数字で入力してください";
        return;
      }
      this.inputReportId = id;

      if (id === String(this.reportId)) {
        // 表示中と同じ日報IDなら取得し直す
        this.errorMessage = "";
        this.fetched.reportId = "";
        this.fetchData(true);
        return;
      }
      this.$router.push("/report/" + id);
    },

    fetchData(needDataFetch) {
      let self = this;

      if (!this.isInitialized) {
        this.fetchBasicData(function () {
          self.fetchData(needDataFetch);
        });
        return;
      }

      if (needDataFetch) {
        this.loading = true;
        this.errorMessage = "";
        const requestedId = this.reportId;

        Promise.resolve()
          .then((a) => {
            return this.checkAuthToken();
          })
        .then((a) => {
          return ReportAPI.getEntry(this.reportId);
        })
        .then((a) => {
          const entrySet = self.normalizeEntrySet(a.data, {});
          const staffCd = entrySet.report.staffCd || entrySet.report.staff_cd || "";
          return UserAPI.profileByStaffCd(staffCd).then(
            (b) => {
              return {
                reportEntity: entrySet,
                profile: b.data,
              };
            }
          );
        })
          .then((data) => {
            self.makeDisplayData(data);
            self.loading = false;
            self.fetched.reportId = self.reportId;

            // 自動の場合にはこのタイミングで既読にする
            if (self.useAutoRead) {
              self.setAlreadyRead(self.reportId);
            }
          })
          .catch(function (err) {
            console.log(err);
            self.loading = false;
            // 取得中に別の日報IDへ移った場合は、古い結果を表示しない
            if (requestedId !== self.reportId) return;

            const status = err && err.response && err.response.status;
            if (status === 404) {
              // 範囲外・存在しない日報は区別しない（SPEC-045 BR-2）
              self.errorMessage = "日報ID " + requestedId + " の日報は見つかりません";
            } else {
              self.errorMessage = "データの取得に失敗しました。";
              self.showErrorToast("データの取得に失敗しました。");
            }
          });
      }
    },

    fetchBasicData(finishProc) {
      let self = this;
      self.isInitialized = true;
      if (finishProc) {
        finishProc();
      }
    },

    makeDisplayData(data) {
      let self = this;

      //console.log(data);

      let entrySet = data.reportEntity;
      let profile = data.profile;

      let reportEntry = self.makeReportDisplayData(entrySet);

      // vuex.stateに日報データを保存
      this.$store.commit("setReport", reportEntry);

      let report = entrySet.report;
      let staffCd = report.staffCd || report.staff_cd || "";

      //this.entry = reportEntry;

      this.userEntry.staffCd = staffCd;
      this.userEntry.userName = profile.user.name;
      this.userEntry.groupName = profile.groupName || profile.group_name;
    },
  },
};
</script>
